import { intl } from '@fullstory/intl';
import { VirtualDashboardId } from '@fullstory/proto/fs/services/dx-objects';
import { fullstorySelectors } from '@fullstory/redux';
import { getDomainFromHost } from '@fullstory/util/url';
import { useSelector } from 'react-redux';
import { DashboardTemplateIds } from '../constants';
import { DashboardTemplateData } from '../types';

const { getOrgId } = fullstorySelectors;

/**
 * returns a mapping of human readable template names to VirtualDashboardId and additional data
 * template names will be used as the template id in the url
 */
export const useDashboardTemplateMap = (): Map<DashboardTemplateIds, DashboardTemplateData> => {
  const domain = getDomainFromHost(window.location.host);
  const orgId = useSelector(getOrgId);

  return new Map([
    [
      DashboardTemplateIds.coreWebVitals,
      {
        title: intl.formatMessage({
          defaultMessage: 'Web vitals dashboard',
          id: 'fx7/ou',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Use this dashboard to track web vitals relevant to engineers.',
          id: '7v3Uzp',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_CORE_WEB_VITALS,
      },
    ],
    [
      DashboardTemplateIds.webCaptureConfigurationGuide,
      {
        title: intl.formatMessage({
          defaultMessage: 'Web Configuration Guide',
          id: 'xy9V9U',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Learn about web configuration',
          id: 'NGRGxJ',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_WEB_CAPTURE_CONFIGURATION_GUIDE,
      },
    ],
    [
      DashboardTemplateIds.frustrationSignalsOverview,
      {
        title: intl.formatMessage({
          defaultMessage: 'Frustration signals dashboard',
          id: 'j5pucP',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Track frustration signals, including Rage Clicks, error clicks, dead clicks, and thrashed cursors.',
          id: 'pFsglc',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_FRUSTRATION_SIGNALS_OVERVIEW,
      },
    ],
    [
      DashboardTemplateIds.featureEngagement,
      {
        title: intl.formatMessage({
          defaultMessage: 'Feature engagement dashboard',
          id: '+u+6qX',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Understand how users are engaging with specific features.',
          id: 'Ik/A+j',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_FEATURE_ENGAGEMENT,
      },
    ],
    [
      DashboardTemplateIds.productManager,
      {
        title: intl.formatMessage({
          defaultMessage: 'Product manager dashboard',
          id: 'TX+C2x',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Track user engagement and see how a feature, page, or URL is performing.',
          id: 'J/ds45',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_PRODUCT_MANAGER,
      },
    ],
    [
      DashboardTemplateIds.purchaseFlowIssues,
      {
        title: intl.formatMessage({
          defaultMessage: 'Purchase flow issues dashboard',
          id: 'vX32ze',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Spot issues preventing high-intent users from being able to complete their purchases.',
          id: 'qamN3W',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_PURCHASE_FLOW_ISSUES,
      },
    ],
    [
      DashboardTemplateIds.ordersRevenue,
      {
        title: intl.formatMessage({
          defaultMessage: 'Orders and revenue summary',
          id: 'rKy5qX',
        }),
        description: intl.formatMessage(
          {
            defaultMessage:
              'View high-level summary metrics related to orders and revenue, based on the [revenue event]({domain}/ui/{orgId}/settings/events) defined by your company.',
            id: 'hl8bx2',
          },
          {
            domain,
            orgId,
          },
        ),
        id: VirtualDashboardId.TEMPLATE_GALLERY_ORDERS_REVENUE,
        requiresRevenue: true,
      },
    ],
    [
      DashboardTemplateIds.uxDesigner,
      {
        title: intl.formatMessage({
          defaultMessage: 'UX designer dashboard',
          id: 'F7k5fE',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'See quick user experience and engagement stats important to UX designers.',
          id: 'YQi6gt',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_UX_DESIGNER,
      },
    ],
    [
      DashboardTemplateIds.issueMonitoring,
      {
        title: intl.formatMessage({
          defaultMessage: 'Issue monitoring dashboard',
          id: 'lqzIec',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Monitor frustration signals and errors. Fix issues before they become problems.',
          id: '0BCNFs',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_ISSUE_MONITORING,
      },
    ],
    [
      DashboardTemplateIds.marketing,
      {
        title: intl.formatMessage({
          defaultMessage: 'Marketing dashboard',
          id: 'RIxAFz',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'See how users arrive at your digital experience and what they do when they get there.',
          id: 'SoDVdu',
        }),
        id: VirtualDashboardId.TEMPLATE_GALLERY_MARKETING,
      },
    ],
    [
      DashboardTemplateIds.cannedWelcome,
      {
        title: intl.formatMessage({
          defaultMessage: 'Welcome to Fullstory Dashboards!',
          id: 'v11kRA',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'This initial Dashboard will walk you through some of the use cases and visualization types available to you!',
          id: 'el7XJ2',
        }),
        id: VirtualDashboardId.CANNED_WELCOME,
      },
    ],
  ]);
};
